import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import { Box, Button, Chip, LinearProgress, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import {
    defaultReadyFor, exerciseName, renderLessonExercise,
} from '../components/exercises/lessonExerciseRenderer';
import { markExerciseComplete, StudentCourse, StudentLesson } from '../../student/mockData';
import { getLessonExercises } from '../../student/mockLessonContent';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT, yellowButtonSx } from '../../student/studentStyles';

interface StudentDesktopLessonPlayerPageProps {
    course: StudentCourse;
    lesson: StudentLesson;
    exerciseIndex: number;
    finished: boolean;
    onExit: () => void;
    onStep: (index: number) => void;
    onComplete: () => void;
    onStartNextLesson: (lesson: StudentLesson) => void;
    onOpenFeedback: () => void;
}

export default function StudentDesktopLessonPlayerPage({
    course, lesson, exerciseIndex, finished, onExit, onStep, onComplete, onStartNextLesson, onOpenFeedback,
}: StudentDesktopLessonPlayerPageProps) {
    const theme = useTheme();
    const useWideCardLayout = useMediaQuery(theme.breakpoints.up('md'));
    const exercises = React.useMemo(() => getLessonExercises(lesson), [lesson]);
    const safeIndex = Math.min(Math.max(exerciseIndex, 0), Math.max(exercises.length - 1, 0));
    const exercise = exercises[safeIndex];
    const [ready, setReady] = React.useState(() => safeIndex < lesson.completedPrompts || defaultReadyFor(exercise));
    const lessonIndex = course.lessons.findIndex(item => item.id === lesson.id);
    const nextLesson = course.lessons[lessonIndex + 1];
    const progress = finished ? 100 : (safeIndex / exercises.length) * 100;
    const previewingAhead = safeIndex > lesson.completedPrompts;

    React.useEffect(() => {
        setReady(safeIndex < lesson.completedPrompts || defaultReadyFor(exercise));
    }, [exercise, lesson.completedPrompts, safeIndex]);

    const handleContinue = () => {
        // Deep links may preview later designs, but must not mark skipped work complete.
        if (safeIndex <= lesson.completedPrompts) {
            markExerciseComplete(lesson, safeIndex);
        }
        if (safeIndex === exercises.length - 1) {
            onComplete();
        } else {
            onStep(safeIndex + 1);
        }
    };

    const nearbyExercises = exercises.slice(Math.max(0, safeIndex - 1), Math.min(exercises.length, safeIndex + 4));
    const nearbyStart = Math.max(0, safeIndex - 1);

    return (
        <Box sx={{ maxWidth: 1220, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
            <Button
                onClick={onExit}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{ mb: 1.5, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: BLUE, textTransform: 'none' }}
            >
                Back to lessons
            </Button>

            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={{ xs: 1.25, md: 3 }} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE, textTransform: 'uppercase' }}>
                        {course.name}
                    </Typography>
                    <Typography component="h1" sx={{ mt: 0.35, fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 21, md: 25 }, color: NAVY }}>
                        {lesson.name}
                    </Typography>
                </Box>
                <Box sx={{ width: { xs: '100%', md: 340 } }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE }}>LESSON PROGRESS</Typography>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: NAVY }}>
                            {finished ? exercises.length : safeIndex + 1} of {exercises.length}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 8, bgcolor: RING_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: BLUE } }}
                    />
                </Box>
            </Stack>

            {finished ? (
                <Box sx={{ ...cardSx, p: { xs: 3, md: 5 }, textAlign: 'center', maxWidth: 760, mx: 'auto' }}>
                    <Box sx={{ width: 92, height: 92, borderRadius: '50%', bgcolor: `${GREEN}14`, display: 'grid', placeItems: 'center', mx: 'auto' }}>
                        <CelebrationRoundedIcon sx={{ color: GREEN, fontSize: 48 }} />
                    </Box>
                    <Typography sx={{ mt: 2.5, fontFamily: POPPINS, fontWeight: 600, fontSize: 27, color: NAVY }}>Lesson complete!</Typography>
                    <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 14, color: SLATE }}>You finished {lesson.name}.</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" spacing={1.25} sx={{ mt: 3.5 }}>
                        {nextLesson ? (
                            <Button variant="contained" onClick={() => onStartNextLesson(nextLesson)} endIcon={<ArrowForwardRoundedIcon />} sx={{ ...yellowButtonSx, px: 3, py: 1.25 }}>
                                Start next lesson
                            </Button>
                        ) : (
                            <Button variant="contained" onClick={onExit} sx={{ ...yellowButtonSx, px: 3, py: 1.25 }}>Back to course</Button>
                        )}
                        <Button variant="outlined" onClick={onOpenFeedback} startIcon={<RateReviewRoundedIcon />} sx={{ borderColor: RING_TRACK, color: NAVY, fontFamily: INTER, fontWeight: 700, textTransform: 'none', px: 2.5 }}>
                            Teacher feedback
                        </Button>
                    </Stack>
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 760px) minmax(250px, 300px)' }, justifyContent: 'start', gap: 3, alignItems: 'start' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box key={exercise.id}>{renderLessonExercise(exercise, () => setReady(true), useWideCardLayout ? 'desktop' : 'mobile')}</Box>
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 2.5 }}>
                            <Button
                                disabled={safeIndex === 0}
                                onClick={() => onStep(safeIndex - 1)}
                                startIcon={<ArrowBackRoundedIcon />}
                                sx={{ minWidth: 112, fontFamily: INTER, fontWeight: 700, color: BLUE, textTransform: 'none' }}
                            >
                                Previous
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={!ready}
                                onClick={handleContinue}
                                endIcon={<ArrowForwardRoundedIcon />}
                                sx={{ ...yellowButtonSx, py: 1.4 }}
                            >
                                {safeIndex === exercises.length - 1 ? 'Finish lesson' : 'Continue'}
                            </Button>
                        </Stack>
                    </Box>

                    <Stack spacing={2}>
                        <Box sx={{ ...cardSx, p: 2.5 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: BLUE }}>CURRENT ACTIVITY</Typography>
                            <Typography sx={{ mt: 0.65, fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>{exerciseName(exercise)}</Typography>
                            <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 12, lineHeight: 1.5, color: SLATE }}>
                                {previewingAhead
                                    ? 'Previewing this activity. Skipped steps will not be marked complete.'
                                    : 'Complete the activity in the workspace. Your progress is saved when you continue.'}
                            </Typography>
                        </Box>

                        <Box sx={{ ...cardSx, p: 2.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15.5, color: NAVY }}>Lesson steps</Typography>
                                <Chip label={`${lesson.completedPrompts} done`} size="small" sx={{ height: 24, bgcolor: `${GREEN}12`, color: SUCCESS_TEXT, fontFamily: INTER, fontWeight: 700, fontSize: 11 }} />
                            </Stack>
                            <Stack spacing={1.25} sx={{ mt: 2 }}>
                                {nearbyExercises.map((item, offset) => {
                                    const index = nearbyStart + offset;
                                    const isCurrent = index === safeIndex;
                                    const isDone = index < lesson.completedPrompts;
                                    const StatusIcon = isCurrent ? RadioButtonCheckedRoundedIcon : isDone ? CheckCircleRoundedIcon : CircleOutlinedIcon;
                                    return (
                                        <Stack key={item.id} direction="row" spacing={1.1} alignItems="center">
                                            <StatusIcon sx={{ fontSize: 18, color: isCurrent ? BLUE : isDone ? GREEN : RING_TRACK }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontFamily: INTER, fontWeight: isCurrent ? 700 : 600, fontSize: 12, color: isCurrent ? NAVY : SLATE }} noWrap>
                                                    {index + 1}. {exerciseName(item)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', bgcolor: SURFACE_SOFT }}>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, lineHeight: 1.5, color: SLATE }}>
                                    Use Previous to review an earlier step without changing your saved progress.
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Box>
            )}
        </Box>
    );
}
